import { Button, FormGroup, Input, Label, Popover, PopoverHeader, PopoverBody } from 'reactstrap';
import { useState } from 'react';
import { AggregationMode, ColumnType } from '@g17eco/core';
import { TableColumn } from '@g17eco/types/universalTracker';

interface WeightFormulaInputProps {
  mode: AggregationMode;
  value: string;
  onChange: (value: string) => void;
  availableColumns: TableColumn[];
  currentColumnCode: string;
}

export const WeightFormulaInput = ({
  mode,
  value,
  onChange,
  availableColumns,
  currentColumnCode,
}: WeightFormulaInputProps) => {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [showAllColumns, setShowAllColumns] = useState(false);

  const popoverId = `weight-formula-help-${mode}`;

  const togglePopover = () => setPopoverOpen(!popoverOpen);
  const closePopover = () => setPopoverOpen(false);

  // Filter columns that are most likely to be used for weights
  const getFilteredColumns = () => {
    const otherColumns = availableColumns.filter((col) => col.code !== currentColumnCode);

    if (showAllColumns) {
      return otherColumns;
    }

    return otherColumns.filter((col) => col.type === ColumnType.Number || col.type === ColumnType.Percentage);
  };

  const handleColumnClick = (columnCode: string) => {
    const newFormula = value + `{${columnCode}}`;
    onChange(newFormula);
    closePopover();
  };

  const filteredColumns = getFilteredColumns();
  const hiddenColumnsCount = availableColumns.filter(
    (col) => col.code !== currentColumnCode && col.type !== ColumnType.Number && col.type !== ColumnType.Percentage,
  ).length;

  return (
    <FormGroup>
      <Label>
        Weight Formula
        <Button id={popoverId} color='info' size='sm' className='ml-2 px-2 py-1' outline onClick={togglePopover}>
          <i className='fa fa-question me-1' />
          Help
        </Button>
      </Label>

      <Input
        type='text'
        placeholder='e.g., {employees_per_category}'
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />

      <div className='text-muted small mt-1'>
        Use curly braces to reference other column values, e.g., {'{column_code}'}
      </div>

      <Popover target={popoverId} placement='right' isOpen={popoverOpen} toggle={togglePopover}>
        <PopoverHeader>Available Column Codes</PopoverHeader>
        <PopoverBody>
          {availableColumns.length > 0 ? (
            <div>
              <div className='mb-2 text-muted small'>Click to add a column code:</div>

              {filteredColumns.map((col) => (
                <div key={col.code} className='mb-2'>
                  <Button
                    color='link'
                    size='sm'
                    className='p-1 text-left d-block'
                    onClick={() => handleColumnClick(col.code)}
                  >
                    <code className='text-primary'>{'{' + col.code + '}'}</code>
                  </Button>
                  <div className='text-muted small ml-3'>
                    {col.name} <span className='badge badge-light'>{col.type}</span>
                  </div>
                </div>
              ))}

              {!showAllColumns && hiddenColumnsCount > 0 && (
                <div className='mt-3 pt-2 border-top'>
                  <Button color='link' size='sm' className='p-0' onClick={() => setShowAllColumns(true)}>
                    <i className='fa fa-plus me-1' />
                    Show all columns ({hiddenColumnsCount} more)
                  </Button>
                </div>
              )}

              {showAllColumns && (
                <div className='mt-3 pt-2 border-top'>
                  <Button color='link' size='sm' className='p-0' onClick={() => setShowAllColumns(false)}>
                    <i className='fa fa-minus me-1' />
                    Show numbers/percentages only
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div className='text-muted'>No other columns available for reference.</div>
          )}
        </PopoverBody>
      </Popover>
    </FormGroup>
  );
};
